import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "dbhrm-theme"; // "light" | "dark" | "system" (unset = system)

const readStored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch (e) {
    return "system";
  }
};

// "system" means no explicit choice: omit the attribute entirely so CSS's
// prefers-color-scheme media query decides. An explicit "light"/"dark"
// always wins over the OS setting (see theme.css's 3-state convention).
const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
};

const ThemeContext = createContext({ theme: "system", setTheme: () => {} });

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStored);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      if (next === "system") {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch (e) {
      // localStorage unavailable (private mode, etc.) -- theme still
      // applies for this session via React state, just won't persist.
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
